import { t } from '../i18n/nl.js';

// ─────────────────────────────────────────────────────────────────────────
// Centrale foutvertaling voor de webportal (Onderdeel 10).
//
// Spiegel van `src/utils/errorMessages.js` in de app: dezelfde categorieën,
// dezelfde referentievorm, dezelfde regel dat `error.message`, `details`,
// `hint` en constraintnamen nooit rechtstreeks in een component belanden.
// De webportal wordt door staf gebruikt, maar de cliëntpagina's eronder
// niet — en een constraintnaam is voor niemand een bruikbare melding.
//
// Verschil met de app: hier komt `t` uit de web-i18n-module en is er geen
// injectie nodig.
// ─────────────────────────────────────────────────────────────────────────

export const ERROR_CATEGORIES = {
  VALIDATIE: 'validatie',
  VERBINDING: 'verbinding',
  AUTORISATIE: 'autorisatie',
  OPSLAG: 'opslag',
  ONBEKEND: 'onbekend',
};

const AUTORISATIE_CODES = new Set(['42501', 'PGRST301', 'PGRST302', '28000', '28P01']);
const TIJDELIJKE_CODES = new Set(['40001', '40P01', '53300', '53400', '55P03', '57014', '57P01', '08006', '08003']);

function tekstVan(error) {
  if (!error) return '';
  if (typeof error === 'string') return error;
  return [error.message, error.name, error.code].filter(Boolean).join(' ');
}

// Alleen "verbinding" als daar werkelijk een aanwijzing voor is; een
// onbekende fout mag nooit als netwerkprobleem worden gepresenteerd.
function lijktNetwerkfout(error) {
  if (!error) return false;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  const tekst = tekstVan(error).toLowerCase();
  if (!tekst) return false;
  return (
    tekst.includes('failed to fetch')
    || tekst.includes('network request failed')
    || tekst.includes('networkerror')
    || tekst.includes('fetcherror')
    || tekst.includes('load failed')
    || tekst.includes('aborterror')
    || tekst.includes('timeout')
  );
}

export function classifyError(error) {
  if (!error) return ERROR_CATEGORIES.ONBEKEND;
  const code = typeof error.code === 'string' ? error.code : '';
  const status = Number(error.status ?? error.statusCode ?? NaN);

  if (lijktNetwerkfout(error)) return ERROR_CATEGORIES.VERBINDING;
  if (AUTORISATIE_CODES.has(code)) return ERROR_CATEGORIES.AUTORISATIE;
  if (status === 401 || status === 403) return ERROR_CATEGORIES.AUTORISATIE;
  if (code.startsWith('23') || code === 'P0001') return ERROR_CATEGORIES.VALIDATIE;
  if (status === 400 || status === 409 || status === 422) return ERROR_CATEGORIES.VALIDATIE;
  if (TIJDELIJKE_CODES.has(code)) return ERROR_CATEGORIES.OPSLAG;
  if (status === 429 || (status >= 500 && status <= 599)) return ERROR_CATEGORIES.OPSLAG;
  return ERROR_CATEGORIES.ONBEKEND;
}

const REFERENTIE_TEKENS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export function makeErrorReference() {
  let kern = '';
  for (let i = 0; i < 6; i++) {
    kern += REFERENTIE_TEKENS[Math.floor(Math.random() * REFERENTIE_TEKENS.length)];
  }
  return `FES-${kern}`;
}

// Postgres hangt bij een CHECK/unique-violation de volledige mislukte rij aan
// `details` ("Failing row contains (…)"). Dat zijn letterlijk de ingevoerde
// waarden en die horen niet in een logregel of in Sentry.
function redacteerRijinhoud(waarde) {
  if (typeof waarde !== 'string') return waarde;
  return waarde.replace(/Failing row contains \([\s\S]*?\)\.?/gi, 'Failing row contains (…)');
}

// Uitsluitend voor console/Sentry, nooit voor de UI.
export function technicalDetails(error) {
  if (!error) return 'geen foutobject';
  if (typeof error === 'string') return redacteerRijinhoud(error);
  return Object.entries({
    name: error.name,
    code: error.code,
    status: error.status ?? error.statusCode,
    message: error.message,
    details: error.details,
    hint: error.hint,
  })
    .filter(([, waarde]) => waarde !== undefined && waarde !== null && waarde !== '')
    .map(([sleutel, waarde]) => `${sleutel}=${redacteerRijinhoud(String(waarde))}`)
    .join(' | ');
}

/**
 * Vertaalt een fout naar een tekst die veilig getoond mag worden.
 * Retourneert `{ category, reference, title, message, retryable }`.
 */
export function describeError(error, { scope = 'web', log = true, reference } = {}) {
  const category = classifyError(error);
  const ref = reference || makeErrorReference();
  if (log) console.error(`[${scope}] ${ref} :: ${technicalDetails(error)}`);
  return {
    category,
    reference: ref,
    title: t(`errors.${category}.title`),
    message: t(`errors.${category}.message`),
    retryable: category === ERROR_CATEGORIES.VERBINDING || category === ERROR_CATEGORIES.OPSLAG,
  };
}

/** Eén regel voor een toast of foutbalk: melding + referentie. */
export function errorText(error, opties) {
  const beschrijving = describeError(error, opties);
  return `${beschrijving.message} ${t('errors.referenceLabel', { reference: beschrijving.reference })}`;
}
